import { useRouter } from '@tanstack/react-router';
import { lazy, Suspense, type JSX } from 'react';
import { useLocation } from '@/services/router/compat';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { updateBrandWizardDraft } from '../wizard/draft-store';
import { useHasResumableBrandRun } from './resumable-run';
import { useBrandRunTracker } from './use-brand-run-tracker';

const LazyBrandRunSnackbar = lazy(() => import('./brand-run-snackbar').then((m) => ({ default: m.BrandRunSnackbar })));
const STYLES_ROUTE = '/workspaces/$workspaceId/design-studio/global-styles';
const STYLES_PATH = /^\/workspaces\/\d+\/design-studio\/global-styles(\/|$)/;

type HostBodyProps = { workspaceId: number };

function HostBody({ workspaceId }: HostBodyProps): JSX.Element | null {
  const { draft, state } = useBrandRunTracker(workspaceId);
  const safeNavigate = useSafeNavigate();
  const router = useRouter();
  if (draft == null || state == null || draft.toastDismissed === true) return null;
  const goToStyles = () =>
    safeNavigate(router.buildLocation({ to: STYLES_ROUTE, params: { workspaceId: String(workspaceId) } }).href);
  return (
    <Suspense fallback={null}>
      <LazyBrandRunSnackbar
        state={state}
        onAction={goToStyles}
        onClose={() => updateBrandWizardDraft({ toastDismissed: true })}
      />
    </Suspense>
  );
}

/**
 * Mounted once in the authenticated layout. Renders nothing (and loads nothing
 * extra) unless this tab holds a brand generation for the current workspace,
 * and stays out of the way on the Styles page, where the banner covers it.
 */
export function BrandRunSnackbarHost(): JSX.Element | null {
  const { pathname } = useLocation();
  const workspaceId = getWorkspaceIdFromCurrentUrl(pathname);
  // The same predicate the Styles page mounts its wizard on, so the two
  // surfaces never disagree about whether there is a run to show.
  const hasRun = useHasResumableBrandRun(workspaceId);
  if (!hasRun || workspaceId == null) return null;
  if (STYLES_PATH.test(pathname)) return null;
  return <HostBody workspaceId={workspaceId} />;
}
